import { Component, Input, OnInit, output, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { ComptageRuleService } from '../../../../core/services/comptage-rule.service';
import { ComptageRule } from '../../../../core/models/models';

import { MatIconModule } from '@angular/material/icon';
import { ConfirmationDialogComponent } from '../../../../shared/components/dialogs/confirmation-dialog.component';

@Component({
    selector: 'app-comptage-rule-dialog',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, MatIconModule, ConfirmationDialogComponent],
    templateUrl: './comptage-rule-dialog.component.html',
    styleUrl: './comptage-rule-dialog.component.scss'
})
export class ComptageRuleDialogComponent implements OnInit {
    private fb = inject(FormBuilder);
    private ruleService = inject(ComptageRuleService);

    @Input() rule?: ComptageRule;
    close = output<void>();
    saved = output<void>();

    ruleForm: FormGroup;
    isSubmitting = false;
    showDeleteConfirm = signal(false);
    deleteCanProceed = signal(true);

    constructor() {
        this.ruleForm = this.fb.group({
            name: ['', Validators.required],
            minTension: [null, [Validators.required, Validators.min(0)]],
            minPuissance: [null, [Validators.required, Validators.min(0)]],
            maxTension: [null, [Validators.min(0)]],
            maxPuissance: [null, [Validators.min(0)]],
            voltageOptions: this.fb.array([]),
            puissanceOptions: this.fb.array([]),
            active: [false]
        });
    }


    addVoltageOption(value: string = ''): void {
        this.voltageOptions.push(this.fb.control(value, Validators.required));
    }

    removeVoltageOption(index: number): void {
        this.voltageOptions.removeAt(index);
    }

    addPuissanceOption(value: string = ''): void {
        this.puissanceOptions.push(this.fb.control(value, Validators.required));
    }

    removePuissanceOption(index: number): void {
        this.puissanceOptions.removeAt(index);
    }

    get voltageOptions(): FormArray {
        return this.ruleForm.get('voltageOptions') as FormArray;
    }

    get puissanceOptions(): FormArray {
        return this.ruleForm.get('puissanceOptions') as FormArray;
    }

    ngOnInit(): void {
        if (this.rule) {
            const { voltageOptions, puissanceOptions, ...rest } = this.rule;
            this.ruleForm.patchValue(rest);

            if (voltageOptions) {
                voltageOptions.split(',').map(s => s.trim()).filter(s => s !== '').forEach(opt => this.addVoltageOption(opt));
            }
            if (puissanceOptions) {
                puissanceOptions.split(',').map(s => s.trim()).filter(s => s !== '').forEach(opt => this.addPuissanceOption(opt));
            }
            if (this.rule.id) {
                this.ruleService.canDeleteRule(this.rule.id).subscribe({
                    next: (canDelete) => this.deleteCanProceed.set(canDelete),
                    error: () => this.deleteCanProceed.set(false)
                });
            }
        } else {
            // Default initial rows
            this.addVoltageOption();
            this.addPuissanceOption();
        }
    }

    onClose(): void {
        this.close.emit();
    }

    onSubmit(): void {
        if (this.ruleForm.valid) {
            this.isSubmitting = true;
            const formValue = this.ruleForm.value;

            const vOptions = (formValue.voltageOptions as string[])
                .map(v => v.trim())
                .filter(v => v !== '')
                .join(', ');

            const pOptions = (formValue.puissanceOptions as string[])
                .map(p => p.trim())
                .filter(p => p !== '')
                .join(', ');

            const ruleData: ComptageRule = {
                id: this.rule?.id,
                name: formValue.name,
                minTension: formValue.minTension,
                minPuissance: formValue.minPuissance,
                maxTension: formValue.maxTension,
                maxPuissance: formValue.maxPuissance,
                voltageOptions: vOptions,
                puissanceOptions: pOptions,
                active: !!formValue.active
            };

            if (this.rule?.id) {
                this.ruleService.updateRule(this.rule.id, ruleData).subscribe({
                    next: () => this.saved.emit(),
                    error: () => this.isSubmitting = false
                });
            } else {
                this.ruleService.createRule(ruleData).subscribe({
                    next: () => this.saved.emit(),
                    error: () => this.isSubmitting = false
                });
            }
        }
    }

    onDeleteClick(): void {
        if (!this.rule?.id) {
            return;
        }
        this.ruleService.canDeleteRule(this.rule.id).subscribe({
            next: (canDelete) => {
                this.deleteCanProceed.set(canDelete);
                this.showDeleteConfirm.set(true);
            },
            error: () => {
                this.deleteCanProceed.set(false);
                this.showDeleteConfirm.set(true);
            }
        });
    }

    confirmDelete(): void {
        if (!this.rule?.id) {
            return;
        }
        this.isSubmitting = true;
        this.ruleService.deleteRule(this.rule.id).subscribe({
            next: () => this.saved.emit(),
            error: () => {
                this.isSubmitting = false;
                this.showDeleteConfirm.set(false);
            }
        });
    }

    cancelDelete(): void {
        this.showDeleteConfirm.set(false);
    }
}
